/**
 * ScopeHeader — a small toolbar row for each entity editor page (Pages,
 * Menus, PDOs, Schedules) hosting the row's scope pill + Change-scope + Clone
 * buttons (ADR-0068 Phase 7).
 *
 * Renders a compact bar: [ScopePill] [Change scope…] [Clone to my workspace]
 *
 * "Change scope" opens the ChangeScopeDialog. "Clone" POSTs
 * `/tenant/{entityKind}/:id/clone` and navigates the caller to the newly
 * cloned row. Both buttons gate on caller permissions:
 *   - Change scope: caller must be able to edit the row (assertCanEdit rule).
 *   - Clone: any caller who can view the row (which is implicit — they're
 *     already looking at it). Cloned rows become personal to the caller.
 */

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiFetch, ApiRequestError } from "../lib/api.js";
import { useToast } from "./Toast.js";
import { useConfirm } from "./ConfirmDialog.js";
import { ScopePill } from "./ScopePill.js";
import { ChangeScopeDialog } from "./ChangeScopeDialog.js";
import type { ContentScope, ScopedEntityKind } from "../lib/scope.js";

interface ScopeHeaderProps {
  entityKind: ScopedEntityKind;
  entityId: string;
  scope: ContentScope;
  /** The owning team's id — required for a real membership check on team rows. */
  ownerGroupId?: string | null;
  ownerGroupName?: string | null;
  ownerAccountId?: string | null;
  /** Caller's identity, used to decide whether Change-scope is clickable. */
  callerAccountId: string | null;
  callerRole: "admin" | "builder" | "viewer";
  callerGroupIds: string[];
  /** Fired after a successful scope change — parent re-fetches. */
  onScopeChanged: () => void | Promise<void>;
  /**
   * Path prefix to navigate to the cloned row's editor. The hub returns the
   * new row; the caller appends `/${new_id}` to this prefix for navigation.
   * e.g. "/app/build/pages" for Pages.
   */
  cloneEditorPathPrefix?: string;
  /** When true, hides the Clone button (e.g., PDOs are op-driven). */
  disableClone?: boolean;
  /**
   * Opt a kind into a TYPED delete confirmation, echoed to the hub as
   * `confirm_name`.
   *
   * Most scoped kinds delete a row. A study also deletes its tree content and
   * its landed document bytes, so its delete is aimed rather than clicked: the
   * user writes the exact title, and the hub checks it again server-side —
   * this dialog is a courtesy, not the control.
   */
  destructiveConfirmation?: { value: string; label: string; detail: string };
  /**
   * When set, this row does NOT own its scope — it follows another row, and the
   * scope shown is inherited. Change-scope is not offered (there is nothing on
   * this row to change), and the note says which row decides it.
   *
   * Studies are the case this exists for: scope belongs to the major study and
   * every study under it follows. Kept generic — the header serves seven kinds
   * and must not learn what a study family is.
   */
  scopeInheritedFrom?: { note: string; href?: string } | null;
}

const URL_KIND: Record<ScopedEntityKind, string> = {
  page: "pages",
  menu: "menus",
  pdo: "pdos",
  workflow: "workflows",
  saved_query: "queries",
  study: "studies",
  optimize_model: "optimize/models"
};

// Where to navigate after a successful delete. Saved queries uses the Atlas
// Query tray, not a standalone list, so we bounce to that route.
const POST_DELETE_PATH: Record<ScopedEntityKind, string> = {
  page: "/app/workspace/pages",
  menu: "/app/workspace/menus",
  pdo: "/app/workspace/pdos",
  workflow: "/app/workspace/schedules",
  saved_query: "/app/atlas-query",
  study: "/app/studies",
  optimize_model: "/app/optimize"
};

const ENTITY_LABEL: Record<ScopedEntityKind, string> = {
  page: "page",
  menu: "menu",
  pdo: "PDO",
  workflow: "schedule",
  saved_query: "saved query",
  study: "study",
  optimize_model: "optimization model"
};

export function ScopeHeader(props: ScopeHeaderProps) {
  const toast = useToast();
  const confirm = useConfirm();
  const nav = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [cloning, setCloning] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Edit authorization mirrors the hub's canEdit rule (spec §11.1).
  // Admins override; owners can edit their own personal rows; builders in the
  // owning team can edit team rows; builders can edit shared rows.
  const canEdit = (() => {
    if (!props.callerAccountId) return false;
    if (props.callerRole === "admin") return true;
    switch (props.scope) {
      case "personal":
        return props.ownerAccountId === props.callerAccountId;
      case "team":
        // Real membership check: caller must be a builder AND a member of
        // the owning team (via ownerGroupId — not just any team).
        return (
          props.callerRole === "builder" &&
          props.ownerGroupId != null &&
          props.callerGroupIds.includes(props.ownerGroupId)
        );
      case "shared":
        return props.callerRole === "builder";
    }
  })();

  // Deliberately NOT folded into `canEdit`, which also gates Delete: an
  // inherited scope removes the SCOPE gesture from this row and nothing else.
  // A child study is still deletable by anyone who may edit it.
  const canChangeScope = canEdit && !props.scopeInheritedFrom;

  // Clone is only meaningful when the resulting copy wouldn't be identical to
  // what's already in the caller's personal scope. If the row is already
  // personal AND owned by me, cloning is a no-op — hide it.
  const cloneSensible = !(
    props.scope === "personal" && props.ownerAccountId === props.callerAccountId
  );

  async function handleDelete() {
    // Some kinds delete a row; a study deletes its tree content and its landed
    // document bytes as well. Where the caller says so, the confirmation is
    // TYPED — the name has to be written out, not clicked past — and the hub
    // re-checks it server-side. See `destructiveConfirmation`.
    const typed = props.destructiveConfirmation;
    const ok = await confirm.confirm({
      title: `Delete this ${ENTITY_LABEL[props.entityKind]}?`,
      detail: typed
        ? typed.detail
        : "This cannot be undone. Dependents may block the delete.",
      confirmLabel: "Delete",
      destructive: true,
      ...(typed
        ? { requireTypedConfirmation: { value: typed.value, label: typed.label } }
        : {})
    });
    if (!ok) return;
    setDeleting(true);
    try {
      const query = typed ? `?confirm_name=${encodeURIComponent(typed.value)}` : "";
      await apiFetch(
        `/api/tenant/${URL_KIND[props.entityKind]}/${encodeURIComponent(props.entityId)}${query}`,
        { method: "DELETE" }
      );
      toast.push({ kind: "success", message: "Deleted." });
      nav(POST_DELETE_PATH[props.entityKind]);
    } catch (e) {
      toast.push({
        kind: "error",
        message: e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e)
      });
      setDeleting(false);
    }
  }

  async function handleClone() {
    setCloning(true);
    try {
      const r = await apiFetch<{ page?: { id: string }; menu?: { id: string }; query?: { id: string } }>(
        `/api/tenant/${URL_KIND[props.entityKind]}/${encodeURIComponent(props.entityId)}/clone`,
        { method: "POST" }
      );
      // The hub wraps the new row under the entity's singular key.
      const newId = r.page?.id ?? r.menu?.id ?? r.query?.id ?? null;
      if (!newId) throw new Error("Clone returned no id.");
      toast.push({ kind: "success", message: "Cloned to your personal workspace." });
      if (props.cloneEditorPathPrefix) {
        nav(`${props.cloneEditorPathPrefix}/${encodeURIComponent(newId)}`);
      }
    } catch (e) {
      toast.push({
        kind: "error",
        message: e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e)
      });
    } finally {
      setCloning(false);
    }
  }

  return (
    <>
      <div
        className="toolbar"
        style={{
          padding: "6px 10px",
          background: "var(--bg-subtle)",
          borderRadius: 6,
          gap: 10
        }}
      >
        <div className="toolbarGroup" style={{ gap: 8, alignItems: "center" }}>
          <span className="muted" style={{ fontSize: 12 }}>Scope:</span>
          <ScopePill scope={props.scope} groupName={props.ownerGroupName} />
          {props.scopeInheritedFrom && (
            <span className="muted" style={{ fontSize: 12 }}>
              {props.scopeInheritedFrom.href ? (
                <>
                  Follows{" "}
                  <Link to={props.scopeInheritedFrom.href}>{props.scopeInheritedFrom.note}</Link>
                </>
              ) : (
                <>Follows {props.scopeInheritedFrom.note}</>
              )}
            </span>
          )}
        </div>
        <div className="toolbarGroup" style={{ marginLeft: "auto", gap: 6 }}>
          {canChangeScope && (
            <button
              type="button"
              className="btn btnSm"
              onClick={() => setDialogOpen(true)}
              disabled={cloning}
            >
              Change scope…
            </button>
          )}
          {!props.disableClone && cloneSensible && (
            <button
              type="button"
              className="btn btnSm"
              onClick={() => void handleClone()}
              disabled={cloning || deleting}
              title="Create a personal copy of this row that you own."
            >
              {cloning ? "Cloning…" : "Clone to my workspace"}
            </button>
          )}
          {canEdit && (
            <button
              type="button"
              className="btn btnSm btnDanger"
              onClick={() => void handleDelete()}
              disabled={cloning || deleting}
            >
              {deleting ? "Deleting…" : "Delete"}
            </button>
          )}
        </div>
      </div>

      {dialogOpen && (
        <ChangeScopeDialog
          entityKind={props.entityKind}
          entityId={props.entityId}
          currentScope={props.scope}
          currentGroupId={null}
          currentOwnerAccountId={props.ownerAccountId ?? null}
          callerRole={props.callerRole}
          onClose={() => setDialogOpen(false)}
          onChanged={() => {
            toast.push({ kind: "success", message: "Scope updated." });
            void props.onScopeChanged();
          }}
        />
      )}
    </>
  );
}
